import { Link } from 'react-router-dom'

export interface LifecycleTimelineEvent {
  label: string
  at?: string | null
}

export interface LifecycleBoardProps {
  title: string
  currentStatus: string
  owner: string
  missing: string[]
  blocked: string[]
  nextBestAction: string
  nextActionTo?: string
  timeline: LifecycleTimelineEvent[]
}

function fmt(value?: string | null) {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
}

export default function LifecycleBoard({
  title,
  currentStatus,
  owner,
  missing,
  blocked,
  nextBestAction,
  nextActionTo,
  timeline,
}: LifecycleBoardProps) {
  return (
    <section className="rounded-xl border bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">Current status: {currentStatus}</p>
        </div>
        <span className="text-xs rounded-full border px-2 py-1 bg-gray-50 text-gray-700">Owner: {owner}</span>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">What is Missing</div>
          {missing.length ? (
            <ul className="mt-2 text-sm text-gray-700 list-disc pl-4 space-y-1">
              {missing.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-gray-600">No missing conditions.</p>
          )}
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">What is Blocked</div>
          {blocked.length ? (
            <ul className="mt-2 text-sm text-amber-900 list-disc pl-4 space-y-1">
              {blocked.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-amber-800">No blocking conditions.</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-700">Next best action: {nextBestAction}</span>
        {nextActionTo && (
          <Link to={nextActionTo} className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100">
            {nextBestAction}
          </Link>
        )}
      </div>

      <div className="rounded-lg border p-3">
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Timeline</div>
        {!timeline.length && <p className="mt-2 text-sm text-gray-500">No timeline events yet.</p>}
        {!!timeline.length && (
          <div className="mt-2 space-y-2">
            {timeline.map((event, index) => (
              <div key={`${event.label}-${index}`} className="text-sm text-gray-700">
                <span className="font-medium">{event.label}</span>
                {fmt(event.at) && <span className="text-gray-500"> · {fmt(event.at)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}